import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Table, Button, Space, Modal, Form, Input, Switch, Popconfirm, Card, Tag, App, Tooltip, Checkbox, Select, Divider, Alert, TreeSelect, Collapse, Transfer } from 'antd';
import { MailOutlined, PlusOutlined, EditOutlined, DeleteOutlined, SendOutlined, ClusterOutlined, CheckSquareOutlined, FilterOutlined, BellOutlined, DatabaseOutlined, RobotOutlined, ApiOutlined } from '@ant-design/icons';
import { emailNotificationsAPI, targetsAPI, agentsAPI, influxTargetsAPI } from '../services/api';

// Alert categories configuration
const ALERT_CATEGORIES = [
  // Backend Targets
  { key: 'TARGET_ICMP', label: 'ICMP Ping', icon: '🔵', group: 'targets', groupLabel: 'Backend Targets' },
  { key: 'TARGET_TCP', label: 'TCP Port', icon: '🔵', group: 'targets', groupLabel: 'Backend Targets' },
  { key: 'TARGET_SSH', label: 'SSH', icon: '🔵', group: 'targets', groupLabel: 'Backend Targets' },
  { key: 'TARGET_HTTP', label: 'HTTP/HTTPS', icon: '🔵', group: 'targets', groupLabel: 'Backend Targets' },
  { key: 'TARGET_DATABASE', label: 'Database', icon: '🔵', group: 'targets', groupLabel: 'Backend Targets' },
  { key: 'TARGET_MQ', label: 'Message Queue', icon: '🔵', group: 'targets', groupLabel: 'Backend Targets' },
  { key: 'TARGET_WS', label: 'WebSocket', icon: '🔵', group: 'targets', groupLabel: 'Backend Targets' },
  { key: 'TARGET_APP', label: 'Application', icon: '🔵', group: 'targets', groupLabel: 'Backend Targets' },
  { key: 'TARGET_OTHER', label: 'Other Targets', icon: '🔵', group: 'targets', groupLabel: 'Backend Targets' },
  // Agent Monitors
  { key: 'AGENT_PROCESS', label: 'Process', icon: '🟡', group: 'agent', groupLabel: 'Agent Monitors' },
  { key: 'AGENT_SERVICE', label: 'Service', icon: '🟡', group: 'agent', groupLabel: 'Agent Monitors' },
  { key: 'AGENT_RESOURCE', label: 'Resources', icon: '🟡', group: 'agent', groupLabel: 'Agent Monitors' },
  { key: 'AGENT_PM2', label: 'PM2', icon: '🟡', group: 'agent', groupLabel: 'Agent Monitors' },
  { key: 'AGENT_HARDWARE', label: 'Hardware', icon: '🟡', group: 'agent', groupLabel: 'Agent Monitors' },
  { key: 'AGENT_EVENT', label: 'Event Viewer', icon: '🟡', group: 'agent', groupLabel: 'Agent Monitors' },
  // InfluxDB
  { key: 'INFLUX', label: 'InfluxDB Monitor', icon: '🟣', group: 'influx', groupLabel: 'InfluxDB' },
];

// Quick presets
const QUICK_PRESETS = [
  { key: 'all', label: 'All Alerts', description: 'Receive all alert types' },
  { key: 'targets', label: 'Targets Only', description: 'Backend monitoring alerts only', groups: ['targets'] },
  { key: 'agent', label: 'Agent Only', description: 'Agent monitoring alerts only', groups: ['agent'] },
  { key: 'influx', label: 'InfluxDB Only', description: 'InfluxDB monitoring alerts only', groups: ['influx'] },
  { key: 'critical', label: 'Critical Only', description: 'Database, Hardware, Service alerts', categories: ['TARGET_DATABASE', 'AGENT_SERVICE', 'AGENT_HARDWARE'] },
];

const EmailNotifications = () => {
  const { message } = App.useApp();
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingEmail, setEditingEmail] = useState(null);
  const [testLoading, setTestLoading] = useState({});
  const [form] = Form.useForm();
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [quickPreset, setQuickPreset] = useState(null);

  // Specific subscriptions
  const [selectedTargets, setSelectedTargets] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [selectedInflux, setSelectedInflux] = useState([]);
  const [targets, setTargets] = useState([]);
  const [agents, setAgents] = useState([]);
  const [influxTargets, setInfluxTargets] = useState([]);
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [loadingInflux, setLoadingInflux] = useState(false);

  const fetchEmails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await emailNotificationsAPI.getAll();
      if (response.success) {
        setEmails(response.data);
      }
    } catch (error) {
      message.error('Gagal mengambil data email notifikasi');
    } finally {
      setLoading(false);
    }
  }, [message]);

  const fetchTargets = useCallback(async () => {
    setLoadingTargets(true);
    try {
      const response = await targetsAPI.getTargets();
      console.log('fetchTargets response:', response);
      if (response && response.success && Array.isArray(response.data)) {
        setTargets(response.data);
      } else if (Array.isArray(response)) {
        setTargets(response);
      } else {
        console.log('Unexpected response structure:', response);
      }
    } catch (error) {
      console.error('Failed to fetch targets:', error);
    } finally {
      setLoadingTargets(false);
    }
  }, []);

  const fetchAgents = useCallback(async () => {
    setLoadingAgents(true);
    try {
      const response = await agentsAPI.getAll();
      console.log('fetchAgents full response:', JSON.stringify(response, null, 2));
      if (response && response.success && response.data && Array.isArray(response.data.agents)) {
        setAgents(response.data.agents);
      } else if (response && response.success && Array.isArray(response.data)) {
        setAgents(response.data);
      } else if (Array.isArray(response)) {
        setAgents(response);
      } else {
        console.log('Unexpected response structure for agents:', response);
      }
    } catch (error) {
      console.error('Failed to fetch agents:', error);
    } finally {
      setLoadingAgents(false);
    }
  }, []);

  const fetchInfluxTargets = useCallback(async () => {
    setLoadingInflux(true);
    try {
      const response = await influxTargetsAPI.getAll();
      console.log('fetchInfluxTargets response:', response);
      if (response && response.success && Array.isArray(response.data)) {
        setInfluxTargets(response.data);
      } else if (Array.isArray(response)) {
        setInfluxTargets(response);
      } else {
        console.log('Unexpected response structure:', response);
      }
    } catch (error) {
      console.error('Failed to fetch influx targets:', error);
    } finally {
      setLoadingInflux(false);
    }
  }, []);

  useEffect(() => {
    fetchEmails();
  }, [fetchEmails]);

  useEffect(() => {
    if (modalVisible) {
      fetchTargets();
      fetchAgents();
      fetchInfluxTargets();
    }
  }, [modalVisible, fetchTargets, fetchAgents, fetchInfluxTargets]);

  // Group categories by groupLabel for display
  const groupedCategories = useMemo(() => {
    const groups = {};
    ALERT_CATEGORIES.forEach(cat => {
      if (!groups[cat.groupLabel]) {
        groups[cat.groupLabel] = [];
      }
      groups[cat.groupLabel].push(cat);
    });
    return groups;
  }, []);

  const handleQuickPresetChange = (presetKey) => {
    setQuickPreset(presetKey);
    if (presetKey === 'all') {
      setSelectedCategories([]);
    } else {
      const preset = QUICK_PRESETS.find(p => p.key === presetKey);
      if (preset) {
        if (preset.categories) {
          setSelectedCategories(preset.categories);
        } else if (preset.groups) {
          const cats = ALERT_CATEGORIES.filter(c => preset.groups.includes(c.group)).map(c => c.key);
          setSelectedCategories(cats);
        }
      }
    }
  };

  const handleCategoryChange = (categoryKey, checked) => {
    setQuickPreset(null);
    if (checked) {
      setSelectedCategories(prev => [...prev, categoryKey]);
    } else {
      setSelectedCategories(prev => prev.filter(k => k !== categoryKey));
    }
  };

  const handleSelectAllInGroup = (groupLabel, checked) => {
    setQuickPreset(null);
    const groupCats = ALERT_CATEGORIES.filter(c => c.groupLabel === groupLabel).map(c => c.key);
    if (checked) {
      setSelectedCategories(prev => [...new Set([...prev, ...groupCats])]);
    } else {
      setSelectedCategories(prev => prev.filter(k => !groupCats.includes(k)));
    }
  };

  const handleAddEmail = () => {
    setEditingEmail(null);
    setSelectedCategories([]);
    setQuickPreset(null);
    setSelectedTargets([]);
    setSelectedAgents([]);
    setSelectedInflux([]);
    setModalVisible(true);
    form.resetFields();
    form.setFieldsValue({ is_active: true });
  };

  const handleEditEmail = (email) => {
    setEditingEmail(email);
    setSelectedCategories(email.alert_categories || []);
    setSelectedTargets(email.subscribed_targets || []);
    setSelectedAgents(email.subscribed_agents || []);
    setSelectedInflux(email.subscribed_influx || []);
    setQuickPreset(null);
    setModalVisible(true);
    form.setFieldsValue({
      email: email.email,
      is_active: email.is_active
    });
  };

  const handleDeleteEmail = async (id) => {
    try {
      await emailNotificationsAPI.delete(id);
      message.success('Email notifikasi berhasil dihapus');
      fetchEmails();
    } catch (error) {
      message.error('Gagal menghapus email notifikasi');
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        alert_categories: selectedCategories,
        subscribed_targets: selectedTargets,
        subscribed_agents: selectedAgents,
        subscribed_influx: selectedInflux
      };

      if (editingEmail) {
        await emailNotificationsAPI.update(editingEmail.id, payload);
        message.success('Email notifikasi berhasil diperbarui');
      } else {
        await emailNotificationsAPI.create(payload);
        message.success('Email notifikasi berhasil ditambahkan');
      }
      setModalVisible(false);
      form.resetFields();
      setSelectedCategories([]);
      setSelectedTargets([]);
      setSelectedAgents([]);
      setSelectedInflux([]);
      setQuickPreset(null);
      fetchEmails();
    } catch (error) {
      message.error(error.response?.data?.message || 'Gagal menyimpan email notifikasi');
    }
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    form.resetFields();
    setSelectedCategories([]);
    setSelectedTargets([]);
    setSelectedAgents([]);
    setSelectedInflux([]);
    setQuickPreset(null);
  };

  const handleTestEmail = async (id, email) => {
    setTestLoading(prev => ({ ...prev, [id]: true }));
    try {
      await emailNotificationsAPI.test(id);
      message.success(`Test email berhasil dikirim ke ${email}`);
    } catch (error) {
      message.error('Gagal mengirim test email');
    } finally {
      setTestLoading(prev => ({ ...prev, [id]: false }));
    }
  };

  const getSelectedCategoriesSummary = () => {
    if (selectedCategories.length === 0) {
      return <Tag color="default">All Alerts (default)</Tag>;
    }
    const cats = selectedCategories.map(k => {
      const cat = ALERT_CATEGORIES.find(c => c.key === k);
      return cat ? `${cat.icon} ${cat.label}` : k;
    });
    return cats.map((c, i) => <Tag key={i} color="blue">{c}</Tag>);
  };

  const isAllSelectedInGroup = (groupLabel) => {
    const groupCats = ALERT_CATEGORIES.filter(c => c.groupLabel === groupLabel).map(c => c.key);
    return groupCats.every(k => selectedCategories.includes(k));
  };

  const isSomeSelectedInGroup = (groupLabel) => {
    const groupCats = ALERT_CATEGORIES.filter(c => c.groupLabel === groupLabel).map(c => c.key);
    const selectedCount = groupCats.filter(k => selectedCategories.includes(k)).length;
    return selectedCount > 0 && selectedCount < groupCats.length;
  };

  const targetTreeData = useMemo(() => {
    if (!targets || targets.length === 0) return [];
    const grouped = {};
    targets.forEach(t => {
      const type = t.target_type || 'Other';
      if (!grouped[type]) grouped[type] = [];
      grouped[type].push({ title: t.name, value: t.id, key: t.id });
    });
    return Object.entries(grouped).map(([type, children]) => ({
      title: type,
      value: type,
      key: type,
      children
    }));
  }, [targets]);

  // Agent tree data
  const agentOptions = useMemo(() => {
    console.log('agentOptions computed, agents:', agents);
    return (agents || []).map(a => ({
      value: a.agent_id,
      label: a.agent_name || a.agentId || 'Unknown'
    }));
  }, [agents]);

  // Influx tree data
  const influxOptions = useMemo(() => {
    return (influxTargets || []).map(t => ({ value: t.id, label: t.name }));
  }, [influxTargets]);

  const columns = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (email, record) => (
        <Space direction="vertical" size={0}>
          <span style={{ fontWeight: 500 }}>{email}</span>
          <span style={{ fontSize: 12, color: '#666' }}>
            {getSubscriptionSummary(record)}
          </span>
        </Space>
      ),
    },
    {
      title: 'Alert Categories',
      key: 'categories',
      width: 200,
      render: (_, record) => {
        if (!record.alert_categories || record.alert_categories.length === 0) {
          return <Tag color="default">All Categories</Tag>;
        }
        const cats = record.alert_categories.map(k => {
          const cat = ALERT_CATEGORIES.find(c => c.key === k);
          return cat ? { key: k, ...cat } : null;
        }).filter(Boolean);

        const grouped = {};
        cats.forEach(c => {
          if (!grouped[c.groupLabel]) grouped[c.groupLabel] = [];
          grouped[c.groupLabel].push(c);
        });

        return (
          <Space wrap>
            {Object.entries(grouped).map(([group, items]) => (
              <Tooltip key={group} title={`${group}: ${items.map(i => i.label).join(', ')}`}>
                <Tag color={group === 'Backend Targets' ? 'blue' : group === 'Agent Monitors' ? 'gold' : 'purple'}>
                  {items.length} {group.split(' ')[0]}
                </Tag>
              </Tooltip>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Specific Targets',
      key: 'specific_targets',
      width: 200,
      render: (_, record) => {
        const parts = [];
        if (record.subscribed_targets?.length > 0) {
          parts.push(<Tag key="t" color="blue">{record.subscribed_targets.length} Targets</Tag>);
        }
        if (record.subscribed_agents?.length > 0) {
          parts.push(<Tag key="a" color="gold">{record.subscribed_agents.length} Agents</Tag>);
        }
        if (record.subscribed_influx?.length > 0) {
          parts.push(<Tag key="i" color="purple">{record.subscribed_influx.length} Influx</Tag>);
        }
        if (parts.length === 0) {
          return <Tag color="default">All</Tag>;
        }
        return <Space>{parts}</Space>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active) => (
        <Tag color={active ? 'green' : 'red'}>
          {active ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleString('id-ID'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Kirim Test Email">
            <Button
              type="text"
              icon={<SendOutlined />}
              onClick={() => handleTestEmail(record.id, record.email)}
              loading={testLoading[record.id]}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditEmail(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Hapus Email Notifikasi"
            description={`Apakah Anda yakin ingin menghapus email ${record.email}?`}
            onConfirm={() => handleDeleteEmail(record.id)}
            okText="Hapus"
            cancelText="Batal"
          >
            <Tooltip title="Hapus">
              <Button
                type="text"
                icon={<DeleteOutlined />}
                danger
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const getSubscriptionSummary = (record) => {
    const hasSubscription = (record.subscribed_targets?.length > 0) ||
      (record.subscribed_agents?.length > 0) ||
      (record.subscribed_influx?.length > 0);
    const hasCategory = record.alert_categories?.length > 0;

    if (hasSubscription || hasCategory) {
      const parts = [];
      if (hasCategory) parts.push(`${record.alert_categories.length} categories`);
      if (record.subscribed_targets?.length > 0) parts.push(`${record.subscribed_targets.length} targets`);
      if (record.subscribed_agents?.length > 0) parts.push(`${record.subscribed_agents.length} agents`);
      if (record.subscribed_influx?.length > 0) parts.push(`${record.subscribed_influx.length} influx`);
      return parts.join(', ');
    }
    return 'All alerts';
  };

  return (
    <Card
      title={
        <Space>
          <BellOutlined />
          <span>Email Notifications</span>
        </Space>
      }
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAddEmail}>
          Add Email
        </Button>
      }
    >
      <Alert
        message="Pengelompokan Alert"
        description="Pilih kategori alert dan/atau specific targets/agents/influx yang akan diterima oleh setiap penerima email. Kosongkan semua pilihan untuk menerima semua alert."
        type="info"
        showIcon
        icon={<ClusterOutlined />}
        style={{ marginBottom: 16 }}
      />

      <Table
        columns={columns}
        dataSource={emails}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `Total ${total} items`,
        }}
      />

      <Modal
        title={editingEmail ? 'Edit Email Notification' : 'Add Email Notification'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={800}
        footer={[
          <Button key="back" onClick={handleModalCancel}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={handleModalOk}>
            {editingEmail ? 'Update' : 'Create'}
          </Button>,
        ]}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="email"
            label="Email Address"
            rules={[
              { required: true, message: 'Please input email address!' },
              { type: 'email', message: 'Please enter a valid email!' }
            ]}
          >
            <Input placeholder="notification@example.com" />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="Active"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>

          <Divider orientation="left">
            <Space>
              <FilterOutlined />
              Alert Categories
            </Space>
          </Divider>

          <Form.Item
            name="alert_categories"
            label="Pilih kategori alert (kosongkan untuk semua kategori)"
            tooltip="Pilih kategori alert yang akan dikirim ke email ini. Kosongkan untuk menerima semua jenis alert."
          >
            <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 16, background: '#fafafa' }}>
              {/* Quick Presets */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                  <FilterOutlined /> Quick Presets
                </div>
                <Select
                  placeholder="Select quick preset..."
                  value={quickPreset}
                  onChange={handleQuickPresetChange}
                  style={{ width: 200 }}
                  allowClear
                  options={QUICK_PRESETS.map(p => ({
                    value: p.key,
                    label: p.label
                  }))}
                  onClear={() => {
                    setQuickPreset(null);
                    setSelectedCategories([]);
                  }}
                />
              </div>

              <Divider style={{ margin: '12px 0' }} />

              {/* Category Groups */}
              {Object.entries(groupedCategories).map(([groupLabel, categories]) => {
                const allSelected = isAllSelectedInGroup(groupLabel);
                const someSelected = isSomeSelectedInGroup(groupLabel);
                return (
                  <div key={groupLabel} style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                      <Checkbox
                        indeterminate={someSelected}
                        checked={allSelected}
                        onChange={(e) => handleSelectAllInGroup(groupLabel, e.target.checked)}
                      />
                      <span style={{ fontWeight: 600, marginLeft: 8 }}>
                        {groupLabel}
                      </span>
                      <Tag style={{ marginLeft: 8 }} color={groupLabel === 'Backend Targets' ? 'blue' : groupLabel === 'Agent Monitors' ? 'gold' : 'purple'}>
                        {categories.length} types
                      </Tag>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, paddingLeft: 24 }}>
                      {categories.map(cat => (
                        <Checkbox
                          key={cat.key}
                          checked={selectedCategories.includes(cat.key)}
                          onChange={(e) => handleCategoryChange(cat.key, e.target.checked)}
                        >
                          <Space>
                            <span>{cat.icon}</span>
                            <span>{cat.label}</span>
                          </Space>
                        </Checkbox>
                      ))}
                    </div>
                  </div>
                );
              })}

              <Divider style={{ margin: '12px 0' }} />

              {/* Selected Summary */}
              <div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                  <CheckSquareOutlined /> Selected Summary
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {getSelectedCategoriesSummary()}
                </div>
              </div>
            </div>
          </Form.Item>

          <Divider orientation="left">
            <Space>
              <DatabaseOutlined />
              Specific Targets
            </Space>
          </Divider>

          <Form.Item
            label="Pilih specific targets (opsional)"
            tooltip="Kosongkan untuk menerima alert dari semua targets. Pilih specific targets untuk hanya menerima alert dari targets tersebut."
          >
            <div style={{ background: '#fafafa', padding: 16, borderRadius: 8 }}>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                  <DatabaseOutlined /> Backend Targets ({selectedTargets.length} selected)
                </div>
                <TreeSelect
                  value={selectedTargets}
                  onChange={setSelectedTargets}
                  treeData={targetTreeData}
                  placeholder={loadingTargets ? "Loading targets..." : "Select specific targets..."}
                  treeCheckable
                  allowClear
                  style={{ width: '100%' }}
                  maxTagCount={3}
                  showSearch
                  treeNodeFilterProp="title"
                  treeDefaultExpandAll
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                  <RobotOutlined /> Agents ({selectedAgents.length} selected)
                </div>
                <Select
                  value={selectedAgents}
                  onChange={setSelectedAgents}
                  options={agentOptions}
                  placeholder={loadingAgents ? "Loading agents..." : "Select specific agents..."}
                  mode="multiple"
                  allowClear
                  style={{ width: '100%' }}
                  maxTagCount={3}
                  showSearch
                  filterOption={(input, option) =>
                    option.label.toLowerCase().includes(input.toLowerCase())
                  }
                />
              </div>

              <div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                  <ApiOutlined /> InfluxDB Targets ({selectedInflux.length} selected)
                </div>
                <Select
                  value={selectedInflux}
                  onChange={setSelectedInflux}
                  options={influxOptions}
                  placeholder={loadingInflux ? "Loading influx..." : "Select specific influx targets..."}
                  mode="multiple"
                  allowClear
                  style={{ width: '100%' }}
                  maxTagCount={3}
                  showSearch
                  filterOption={(input, option) =>
                    option.label.toLowerCase().includes(input.toLowerCase())
                  }
                />
              </div>

              {(selectedTargets.length > 0 || selectedAgents.length > 0 || selectedInflux.length > 0) && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #e8e8e8' }}>
                  <Tag color="blue">{selectedTargets.length} Targets</Tag>
                  <Tag color="gold">{selectedAgents.length} Agents</Tag>
                  <Tag color="purple">{selectedInflux.length} Influx</Tag>
                </div>
              )}
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default EmailNotifications;